import { useState } from 'react'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { Tooltip } from '@/shared/components/Tooltip'
import type { ServeMode, ServeOverrideAction } from '@/lib/apiTypes'

/**
 * Shared tri-state serve-override control (migration 093): Auto (policy decides) /
 * Block (force-refuse regardless of policy) / Allow (serve even when policy-blocked).
 * Used at provider, provider-version, module, module-version and state-workspace level.
 *
 * Auto applies instantly; Block and Allow open a ConfirmModal with an optional reason
 * (the durable who/when/why record is the server-side audit event). The whole cluster
 * stops click propagation so it can sit inside clickable table rows.
 */
/** The toast copy for a completed serve-override, so the three outcomes read identically wherever
 *  the control is used. `scope` names what the override covers when that is not obvious from the
 *  page - providers are overridden per org, module versions are not. */
export function serveOverrideToast(subject: string, action: ServeOverrideAction, scope?: string): string {
  if (action === 'block') return `${subject} force-blocked${scope ? ` for this ${scope}` : ''}.`
  if (action === 'allow') return `${subject} force-allowed - serves even if policy-blocked.`
  return `${subject} back to Auto - policies decide again.`
}

export function ServeOverrideControl({ value, subject, busy, compact, onApply }: {
  value: ServeOverrideAction
  /** Human name of the thing being overridden, e.g. "hashicorp/aws" or "v5.1.0". */
  subject: string
  busy?: boolean
  /** Tighter paddings for dense table rows. */
  compact?: boolean
  onApply: (action: ServeOverrideAction, reason?: string) => void | Promise<void>
}) {
  const [confirm, setConfirm] = useState<'block' | 'allow' | null>(null)
  const [reason, setReason] = useState('')

  const options: Array<{ key: ServeOverrideAction; label: string; tip: string; activeStyle: React.CSSProperties }> = [
    {
      key: null,
      label: 'Auto',
      tip: 'Policy decides: serving follows your org policies and scan verdicts (the default).',
      activeStyle: {},
    },
    {
      key: 'block',
      label: 'Block',
      tip: `Fail-safe: refuse to serve ${subject} to this org regardless of policy. Consumers get a 404 / refused apply.`,
      activeStyle: { background: 'var(--red)', borderColor: 'var(--red)', color: '#fff' },
    },
    {
      key: 'allow',
      label: 'Allow',
      tip: `Force-serve ${subject} even when an enforcing policy blocks it. Overrides policy enforcement.`,
      activeStyle: { background: 'var(--green)', borderColor: 'var(--green)', color: '#fff' },
    },
  ]

  function pick(action: ServeOverrideAction) {
    if (action === value || busy) return
    if (action === null) {
      void onApply(null)
      return
    }
    setReason('')
    setConfirm(action)
  }

  const btnPad = compact ? '1px 7px' : '2px 9px'
  const btnFont = compact ? '0.66rem' : '0.72rem'

  return (
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center' }}>
      <span role="group" aria-label={`Serve override for ${subject}`} style={{ display: 'inline-flex', gap: 2 }}>
        {options.map((o) => {
          const active = value === o.key
          return (
            <Tooltip key={o.label} text={o.tip} placement="top">
              <button
                type="button"
                className={`btn btn-sm ${active && o.key === null ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={active}
                disabled={busy}
                style={{ padding: btnPad, fontSize: btnFont, ...(active ? o.activeStyle : {}) }}
                onClick={() => pick(o.key)}
              >
                {o.label}
              </button>
            </Tooltip>
          )
        })}
      </span>
      {confirm && (
        <ConfirmModal
          track="provider_serve_override_confirm"
          title={confirm === 'block' ? `Force-block ${subject}?` : `Force-allow ${subject}?`}
          message={
            <>
              {confirm === 'block' ? (
                <>
                  No version of <strong>{subject}</strong> will be served to this org while
                  blocked - downloads and <code className="mono">tofu init</code> fail regardless
                  of policy verdicts. This is the fail-safe; flip back to Auto to lift it.
                </>
              ) : (
                <>
                  <strong>{subject}</strong> will be served even when an enforcing policy blocks
                  it. This overrides policy enforcement for this org - record why below.
                </>
              )}
              <textarea
                className="input"
                placeholder="Reason (optional, audited)"
                value={reason}
                rows={2}
                style={{ display: 'block', width: '100%', marginTop: 12, resize: 'vertical' }}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          }
          confirmLabel={confirm === 'block' ? 'Force-block' : 'Force-allow'}
          danger={confirm === 'block'}
          busy={busy}
          onConfirm={async () => {
            await onApply(confirm, reason.trim() || undefined)
            setConfirm(null)
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </span>
  )
}

/**
 * Single artefact-level serve-mode control (migration 095): Off / Serve / Block. Off and Serve apply
 * instantly; Block opens a ConfirmModal (audited reason) as it's the fail-safe. Renders for managed
 * AND discovered rows (Block on a discovered provider is valid). Stops click propagation so it can
 * sit inside clickable table rows.
 */
export function ServeModeControl({ value, subject, busy, compact, serveDisabledReason, onApply }: {
  value: ServeMode
  subject: string
  busy?: boolean
  compact?: boolean
  /** When set, the Serve option is disabled and this replaces its tooltip (tier gate: serving
   *  is Business+). Off and Block stay available on every tier. */
  serveDisabledReason?: string | null
  onApply: (mode: ServeMode, reason?: string) => void | Promise<void>
}) {
  const [confirmBlock, setConfirmBlock] = useState(false)
  const [reason, setReason] = useState('')

  const options: Array<{ key: ServeMode; label: string; tip: string; activeStyle: React.CSSProperties }> = [
    {
      key: 'off',
      label: 'Off',
      tip: `Don't serve ${subject}. Config is kept but nothing is served - tofu init won't resolve it.`,
      activeStyle: { background: 'var(--text3)', borderColor: 'var(--text3)', color: 'var(--bg)' },
    },
    {
      key: 'serve',
      label: 'Serve',
      tip: `Serve ${subject} to this org: upstream versions plus (Business+) terramantle's own builds, selectable by exact pin. Policies still apply.`,
      activeStyle: { background: 'var(--green)', borderColor: 'var(--green)', color: '#fff' },
    },
    {
      key: 'block',
      label: 'Block',
      tip: `Fail-safe: hard-refuse ${subject} to this org regardless of policy. Consumers get a 404.`,
      activeStyle: { background: 'var(--red)', borderColor: 'var(--red)', color: '#fff' },
    },
  ]

  function pick(mode: ServeMode) {
    if (mode === value || busy) return
    if (mode === 'block') { setReason(''); setConfirmBlock(true); return }
    void onApply(mode)
  }

  const btnPad = compact ? '1px 7px' : '2px 9px'
  const btnFont = compact ? '0.66rem' : '0.72rem'

  return (
    <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center' }}>
      <span role="group" aria-label={`Serve mode for ${subject}`} style={{ display: 'inline-flex', gap: 2 }}>
        {options.map((o) => {
          const active = value === o.key
          const gated = o.key === 'serve' && !!serveDisabledReason && !active
          return (
            <Tooltip key={o.label} text={gated ? serveDisabledReason! : o.tip} placement="top">
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                aria-pressed={active}
                aria-disabled={gated || undefined}
                disabled={busy || gated}
                style={{ padding: btnPad, fontSize: btnFont, ...(active ? o.activeStyle : {}) }}
                onClick={() => pick(o.key)}
              >
                {o.label}
              </button>
            </Tooltip>
          )
        })}
      </span>
      {confirmBlock && (
        <ConfirmModal
          track="provider_serve_block_confirm"
          title={`Block ${subject}?`}
          message={
            <>
              No version of <strong>{subject}</strong> will be served to this org while blocked -
              downloads and <code className="mono">tofu init</code> fail regardless of policy
              verdicts. This is the fail-safe; set back to Serve to lift it.
              <textarea
                className="input"
                placeholder="Reason (optional, audited)"
                value={reason}
                rows={2}
                style={{ display: 'block', width: '100%', marginTop: 12, resize: 'vertical' }}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          }
          confirmLabel="Block"
          danger
          busy={busy}
          onConfirm={async () => {
            await onApply('block', reason.trim() || undefined)
            setConfirmBlock(false)
          }}
          onCancel={() => setConfirmBlock(false)}
        />
      )}
    </span>
  )
}

/** Compact badge for a non-serving artefact serve_mode (off/block), next to the provider name. */
export function ServeModeBadge({ mode }: { mode: ServeMode }) {
  // Only an explicit block earns a badge. 'off' is the default state for most providers and a
  // "not served" tag on every row was noise, not signal - the serve control already shows it.
  if (mode !== 'block') return null
  return (
    <Tooltip text="Blocked by an org admin. Nothing serves until set back to Serve." placement="top">
      <button
        type="button"
        className="badge badge-red"
        style={{ cursor: 'help', margin: 0 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
      >
        blocked
      </button>
    </Tooltip>
  )
}

/** Compact badge for an active override, for placing next to the version string. */
export function ServeOverrideBadge({ value, reason }: { value: ServeOverrideAction; reason?: string | null }) {
  if (!value) return null
  const blocked = value === 'block'
  return (
    <Tooltip
      text={blocked
        ? `Force-blocked by an org admin${reason ? `: ${reason}` : ''}. Nothing serves until set back to Auto.`
        : `Force-allowed by an org admin${reason ? `: ${reason}` : ''}. Serves even if policy-blocked.`}
      placement="top"
    >
      <button
        type="button"
        className={`badge ${blocked ? 'badge-red' : 'badge-green'}`}
        style={{ cursor: 'help', margin: 0 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
      >
        {blocked ? 'force-blocked' : 'force-allowed'}
      </button>
    </Tooltip>
  )
}
